/**
 * ChargeActivityCard - Card unificado de cobranças na timeline (cliente e negócio)
 *
 * @version 1.0.0
 * RELEASE: 2026-08-20 v1.0.0 - Criação. Renderiza tanto a cobrança avulsa
 *   (valor, vencimento, parcelas, meio de pagamento) quanto o card AGRUPADO
 *   de todas as cobranças de uma mesma assinatura (total, faixa de vencimentos,
 *   chips de status e lista de ciclos expansível).
 *
 * IMPORTANTE: todos os agregados vêm PRONTOS do backend (RPCs
 * get_customer_activities_paginated / get_deal_activities_paginated), dentro de
 * `new_value`. O front apenas formata — sem cálculo/fetch extra.
 */
import React, { useState } from 'react';
import { format } from 'date-fns';
import { CalendarClock, ChevronDown, ChevronRight, DollarSign, Layers } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** Rótulos legíveis dos meios de pagamento */
const TIPO_PAGAMENTO_LABELS: Record<string, string> = {
  pix: 'PIX',
  boleto: 'Boleto',
  cartao_credito: 'Cartão Crédito',
  cartao_debito: 'Cartão Débito',
  indefinido: 'Pergunte ao Cliente',
  dinheiro: 'Dinheiro',
  ted: 'TED',
  doc: 'DOC',
  carne: 'Carnê',
  cheque: 'Cheque',
  nota_promissoria: 'Nota Promissória',
};

/** Rótulos + cores por status da cobrança */
const STATUS_META: Record<string, { label: string; className: string }> = {
  pago: { label: 'Pago', className: 'text-emerald-700 dark:text-emerald-400 border-emerald-300/60' },
  pendente: { label: 'Pendente', className: 'text-amber-700 dark:text-amber-400 border-amber-300/60' },
  aguardando_emissao: { label: 'Aguardando emissão', className: 'text-sky-700 dark:text-sky-400 border-sky-300/60' },
  vencido: { label: 'Vencido', className: 'text-red-700 dark:text-red-400 border-red-300/60' },
  cancelado: { label: 'Cancelado', className: 'text-muted-foreground border-border' },
  estornado: { label: 'Estornado', className: 'text-muted-foreground border-border' },
  desconhecido: { label: 'Sem status', className: 'text-muted-foreground border-border' },
};

/** Item de ciclo/cobrança agregado devolvido pelo backend */
interface ChargeGroupItem {
  ciclo: number | null;
  valor: number | string | null;
  vencimento: string | null;
  status: string | null;
  cobranca_code: string | null;
}

interface ChargeActivityCardProps {
  /** Payload `new_value` da atividade charge_generated */
  newValue: Record<string, any> | null | undefined;
}

/** Formata número em moeda BRL */
const brl = (v: unknown) =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Formata data ISO (yyyy-MM-dd) sem sofrer shift de timezone */
const fmtDate = (d?: string | null) => {
  if (!d) return null;
  const iso = String(d).slice(0, 10);
  return format(new Date(`${iso}T00:00:00`), 'dd/MM/yyyy');
};

export const ChargeActivityCard: React.FC<ChargeActivityCardProps> = ({ newValue }) => {
  const [expanded, setExpanded] = useState(false);

  if (!newValue) return null;

  const nv = newValue;
  const isGrouped = nv.grouped === true;

  // ---------------------------------------------------------------------
  // Modo AVULSO: cobrança única (mantém as badges já usadas na timeline)
  // ---------------------------------------------------------------------
  if (!isGrouped) {
    const tipoPagamento =
      nv.tipo_pagamento && nv.tipo_pagamento !== 'N/A'
        ? TIPO_PAGAMENTO_LABELS[String(nv.tipo_pagamento)] || String(nv.tipo_pagamento)
        : null;
    const statusMeta = nv.status ? STATUS_META[String(nv.status)] : null;

    return (
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
          <DollarSign className="h-3 w-3" />
          {brl(nv.valor_total)}
        </Badge>
        {Number(nv.numero_parcelas) > 1 && (
          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
            {nv.numero_parcelas}x parcelas
          </Badge>
        )}
        {tipoPagamento && (
          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
            {tipoPagamento}
          </Badge>
        )}
        {nv.data_vencimento && (
          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
            <CalendarClock className="h-3 w-3" />
            Venc. {fmtDate(nv.data_vencimento)}
          </Badge>
        )}
        {statusMeta && (
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0.5', statusMeta.className)}>
            {statusMeta.label}
          </Badge>
        )}
        {nv.cobranca_code && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 text-muted-foreground">
            #{nv.cobranca_code}
          </Badge>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Modo AGRUPADO: várias cobranças da mesma assinatura em um único card
  // ---------------------------------------------------------------------
  const items: ChargeGroupItem[] = Array.isArray(nv.items) ? nv.items : [];
  const statusBreakdown: Record<string, number> = nv.status_breakdown || {};
  const count = Number(nv.count || items.length || 0);
  const vencMin = fmtDate(nv.vencimento_min);
  const vencMax = fmtDate(nv.vencimento_max);
  const tipoPagamento =
    nv.tipo_pagamento && nv.tipo_pagamento !== 'N/A'
      ? TIPO_PAGAMENTO_LABELS[String(nv.tipo_pagamento)] || String(nv.tipo_pagamento)
      : null;

  return (
    <div className="mt-1.5 rounded-md border border-border/60 bg-muted/30 px-2.5 py-2 space-y-2">
      {/* Resumo agregado */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
          <Layers className="h-3 w-3" />
          {count} cobranças
        </Badge>
        <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
          <DollarSign className="h-3 w-3" />
          Total {brl(nv.valor_total_soma)}
        </Badge>
        {(vencMin || vencMax) && (
          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
            <CalendarClock className="h-3 w-3" />
            {vencMin === vencMax ? `Venc. ${vencMin}` : `Venc. ${vencMin} a ${vencMax}`}
          </Badge>
        )}
        {tipoPagamento && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
            {tipoPagamento}
          </Badge>
        )}
      </div>

      {/* Chips de status */}
      {Object.keys(statusBreakdown).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {Object.entries(statusBreakdown).map(([status, qtd]) => {
            const meta = STATUS_META[status] || STATUS_META.desconhecido;
            return (
              <Badge
                key={status}
                variant="outline"
                className={cn('text-[10px] px-1.5 py-0.5', meta.className)}
              >
                {qtd} {meta.label.toLowerCase()}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Lista de ciclos (já veio pronta do backend) */}
      {items.length > 0 && (
        <div>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[10px] text-primary/80 hover:text-primary"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {expanded ? 'Ocultar cobranças' : `Ver cobranças (${items.length}${count > items.length ? ` de ${count}` : ''})`}
          </button>

          {expanded && (
            <div className="mt-1.5 divide-y divide-border/50 rounded-md border border-border/50 bg-background/60">
              {items.map((item, idx) => {
                const meta = STATUS_META[String(item.status || 'desconhecido')] || STATUS_META.desconhecido;
                return (
                  <div
                    key={`${item.cobranca_code || 'item'}-${idx}`}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 text-[11px]"
                  >
                    <span className="text-muted-foreground shrink-0">
                      {item.ciclo != null && item.ciclo > 0 ? `Ciclo ${item.ciclo}` : 'Pró-rata'}
                    </span>
                    <span className="text-muted-foreground shrink-0">{fmtDate(item.vencimento) || '—'}</span>
                    <span className="font-medium text-foreground shrink-0">{brl(item.valor)}</span>
                    <Badge variant="outline" className={cn('text-[9px] px-1 py-0 shrink-0', meta.className)}>
                      {meta.label}
                    </Badge>
                  </div>
                );
              })}
              {count > items.length && (
                <div className="px-2 py-1.5 text-[10px] text-muted-foreground">
                  +{count - items.length} cobranças não exibidas
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChargeActivityCard;
